import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeroContent {
  heading: string;
  highlight: string;
  image: string;
  altText: string;
}

const heroContent: HeroContent[] = [
  {
    heading: "Fresh trim for",
    highlight: "the weekend",
    image: "/assets/equipment-optimized.webp",
    altText: "Professional barber equipment and tools displayed on a clean surface, showcasing the precision tools used for haircuts"
  },
  {
    heading: "Style that",
    highlight: "defines you",
    image: "/assets/dream-chair-optimized.webp",
    altText: "Modern barber chair in a stylish salon setting, ready for clients to experience premium grooming services"
  },
  {
    heading: "Precision in",
    highlight: "every cut",
    image: "/assets/clipping-optimized.webp",
    altText: "Barber performing a precise haircut, demonstrating expert technique and attention to detail"
  }
];

const HeroHeading: React.FC<{ content: HeroContent }> = ({ content }) => (
  <div className="relative hero-heading py-8 md:py-6">
    <h1 className="text-left md:text-left font-bold text-gray-800 dark:text-gray-100 
      text-[clamp(36px,3vw,48px)] 
      md:text-[clamp(36px,4vw,48px)] 
      lg:text-[clamp(48px,5vw,64px)]
      leading-[1.2]
      font-['Instrument_Serif']"
    >
      {content.heading}{' '}
      <span className="bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
        {content.highlight}
      </span>
    </h1>
    
  </div>
);

const ImageGrid: React.FC<{ activeIndex: number; setActiveIndex: (index: number) => void }> = ({ 
  activeIndex, 
  setActiveIndex 
}) => {
  return (
    <div className="flex flex-col md:flex-row gap-4">
      {heroContent.map((content, index) => (
        <div 
          key={index}
          className={
            `
            relative
            overflow-hidden
            rounded-xl
            transition-all
            duration-700
            ${index === activeIndex ? 'md:w-[60%]' : 'md:w-[20%]'}
            ${index === 1 ? 'md:-translate-x-2' : ''}
            ${index === 2 ? 'md:-translate-x-4' : ''}
            h-[calc(20vh-15px)] md:h-[calc(60vh-15px)]
            ${index !== activeIndex ? 'max-h-[calc(110px-15px)] md:max-h-[calc(100vh-15px)]' : ''}
            `
          }
        >
          <img
            src={content.image}
            alt={content.altText}
            onClick={() => setActiveIndex(index)}
            className={`
              hero-image
              cursor-pointer
              w-full
              h-full
              object-cover
              shadow-lg
              ${index === activeIndex ? 'active opacity-100 scale-100' : 'opacity-40 hover:opacity-60 scale-98'}
            `}
            loading="lazy"
          />
        </div>
      ))}
    </div>
  );
};

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section className="py-12 md:py-6">
    {children}
  </section>
);

const Hero: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroContent.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Section>
      <div className="relative w-full min-h-[80vh] md:min-h-screen pt-4 md:pt-6 flex flex-col justify-center">
        <div className="container mx-auto px-4">
          <HeroHeading content={heroContent[activeIndex]} />
          <ImageGrid activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
          <p className="mt-8 text-gray-600 dark:text-gray-300 text-xl md:text-2xl font-['Instrument_Serif']">
            Conveniently located near Shepherd's Bush Market station, 3D Afro and European Unisex Salon offers a range of affordable hair services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <button
              onClick={() => document.getElementById('find-us')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105"
            >
              Find Us
            </button>
            <button
              onClick={() => navigate('/booking')}
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-gray-900 font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:scale-105 inline-flex items-center justify-center"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Hero;